import { useState, useEffect, useRef } from 'react'
import classNames from 'classnames'

import { galleryData } from 'resources'
import { SectionLayout } from 'components/layouts'
import { useLockBodyScroll } from 'hooks'

import HexagonIcon from 'assets/images/hexagon.png'
import { ReactComponent as ArrowIcon } from 'assets/svg/arrow.svg'
import { ReactComponent as CloseIcon } from 'assets/svg/close.svg'

import styles from './Gallery.module.scss'

const Gallery = () => {
  const sliderRef = useRef<HTMLDivElement>(null)
  const modalRef = useRef<HTMLDivElement>(null)

  const [isOpen, setIsOpen] = useState(false)
  const [sliderIndex, setSliderIndex] = useState(0)
  const [currentIndex, setCurrentIndex] = useState(0)

  useLockBodyScroll(isOpen)

  const scrollToCurrentIndex = () => {
    if (sliderRef.current) {
      const imageWidth = sliderRef.current.querySelector('img')?.offsetWidth || 0
      const scrollPosition = currentIndex * imageWidth

      sliderRef.current.scrollTo({
        left: scrollPosition,
        behavior: 'smooth',
      })
    }
  }

  const handlePrev = () => {
    setCurrentIndex(prevIndex =>
      prevIndex === 0
        ? galleryData.length - 1
        : prevIndex > galleryData.length - 3
        ? galleryData.length - 4
        : prevIndex - 1
    )
  }

  const handleNext = () => {
    setCurrentIndex(prevIndex => (prevIndex + 3 >= galleryData.length ? 0 : prevIndex + 1))
  }

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex(prevIndex => {
        const newIndex = prevIndex + 1 >= galleryData.length ? 0 : prevIndex + 1

        return newIndex
      })
    }, 6000)

    return () => clearInterval(interval)
  }, [])

  useEffect(() => {
    scrollToCurrentIndex()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex])

  const renderImagesList = galleryData.map((item, index) => (
    <div
      key={item.id}
      role='button'
      onClick={() => {
        setIsOpen(true)
        setSliderIndex(index)
      }}
    >
      <img src={item.url} alt='gallery image' />
    </div>
  ))

  return (
    <SectionLayout id='GALLERY' className={styles.wrapper}>
      <div>
        <h2 className={styles.title}>Gallery</h2>
        <div className={classNames(styles.controls, 'container')}>
          <a
            target='_blanck'
            rel='noreferrer'
            href='https://drive.google.com/drive/folders/1p9gPKyY2-Q6DTxIy5zaJ3skMuAizyIWv'
            className={styles.button}
          >
            See all pictures
          </a>
          <div>
            <button className={styles.controls__left} onClick={handlePrev}>
              <ArrowIcon />
              <p>Prev</p>
            </button>
            <button className={styles.controls__right} onClick={handleNext}>
              <p>Next</p>
              <ArrowIcon />
            </button>
          </div>
        </div>
        <div ref={sliderRef} className={styles.wrapper__content}>
          {renderImagesList}
        </div>
      </div>

      {isOpen && (
        <div className={styles.modal}>
          <div className={styles.modal__wrapper}>
            <div role='button' className={styles.modal__wrapper_close} onClick={() => setIsOpen(false)}>
              <CloseIcon />
            </div>

            <div
              role='button'
              className={styles.modal__wrapper_arrow}
              onClick={() => {
                if (sliderIndex === 0) {
                  return null
                }

                setSliderIndex(sliderIndex - 1)
              }}
            >
              <ArrowIcon />
            </div>

            <div className={styles.modal__content} ref={modalRef}>
              <img src={galleryData[sliderIndex].url} alt='slider image' />
            </div>

            <div
              role='button'
              className={styles.modal__wrapper_arrow}
              onClick={() => {
                if (sliderIndex >= galleryData.length - 1) {
                  setSliderIndex(0)
                } else {
                  setSliderIndex(sliderIndex + 1)
                }
              }}
            >
              <ArrowIcon />
            </div>
          </div>
        </div>
      )}

      <img className={styles.banner__top} src={HexagonIcon} alt='logo' />
      <img className={styles.banner__bottom} src={HexagonIcon} alt='logo' />
    </SectionLayout>
  )
}

export default Gallery
